'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Select, { OptionProps, SingleValue, SingleValueProps, StylesConfig } from 'react-select';
import { BOARD_CONFIG } from '@/(routers)/boards/editor/block/blockConfig';
import { BOARD } from '@/(routers)/boards/editor/block/definitions/boards';

const CONTROL_HEIGHT = 70;
export type BoardValue = 'orange' | 'blue' | null;

export interface OptionType {
    value: 'orange' | 'blue';
    id: string;
    label: string;
    imageSrc: string;
    bgColor: string;
}

const STATIC_OPTIONS: OptionType[] = [
    {
        value: 'orange',
        id: 'ORANGE',
        label: 'スクーミーオレンジボード',
        imageSrc: BOARD.orange.icon.src,
        bgColor: 'rgba(255, 128, 0, 0.3)',
    },
    {
        value: 'blue',
        id: 'BLUE',
        label: 'スクーミーブルーボード',
        imageSrc: BOARD.blue.icon.src,
        bgColor: 'rgba(47, 90, 164, 0.3)',
    },
];

const BoardIcon: React.FC<{ data: OptionType }> = ({ data }) => (
    <div
        className="flex justify-center items-center rounded-full"
        style={{ width: 40, height: 40, backgroundColor: data.bgColor }}
    >
        <img
            src={data.imageSrc}
            alt=""
            style={{ width: 32, height: 32, objectFit: 'contain', flexShrink: 0 }}
        />
    </div>
);

const SelectedView: React.FC<SingleValueProps<OptionType>> = ({ data }) => {
    return (
        <div className="flex items-center">
            <div className="flex justify-center items-center px-1">
                <BoardIcon data={data} />
            </div>
            <div className="flex flex-grow justify-start font-corp font-medium text-color-base pl-2 pr-8">
                {data.label}
            </div>
        </div>
    );
};

const OptionItem: React.FC<OptionProps<OptionType>> = (props) => {
    const { data, innerRef, innerProps, isFocused } = props;

    return (
        <div
            ref={innerRef}
            {...innerProps}
            style={{
                backgroundColor: isFocused ? '#EFEFEF' : '#FFFFFF',
                cursor: 'pointer',
            }}
            className="flex justify-start items-center px-2 py-[16px]"
        >
            <div className="flex justify-center items-center px-1">
                <BoardIcon data={data} />
            </div>
            <div className="flex flex-grow justify-start font-corp font-medium text-color-base pl-2 pr-8">
                {data.label}
            </div>
        </div>
    );
};

const customStyles: StylesConfig<OptionType, false> = {
    menuPortal: (base) => ({
        ...base,
        zIndex: 999999,
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 999999,
    }),
    input: (provided) => ({
        ...provided,
        fontSize: '16px',
        margin: 0,
        padding: 0,
    }),
    valueContainer: (provided) => ({
        ...provided,
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
    }),
    control: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#FFFFFF' : '#F7F7F7',
        borderColor: state.isFocused ? '#8E8E8E' : '#FFFFFF',
        borderWidth: '1px',
        borderRadius: 0,
        boxShadow: 'none',
        cursor: 'pointer',
        minHeight: CONTROL_HEIGHT,
        height: CONTROL_HEIGHT,
        '&:hover': {
            borderColor: state.isFocused ? '#8E8E8E' : '#FFFFFF',
        },
    }),
    option: (provided) => ({
        ...provided,
        cursor: 'pointer',
    }),
    dropdownIndicator: (provided, state) => ({
        ...provided,
        display: state.selectProps.isDisabled ? 'none' : provided.display,
    }),
    indicatorSeparator: (provided, state) => ({
        ...provided,
        display: state.selectProps.isDisabled ? 'none' : provided.display,
    }),
    singleValue: (provided) => ({
        ...provided,
        margin: 0,
    }),
    placeholder: (provided) => ({
        ...provided,
        margin: 0,
    }),
};

export interface BoardSelectorManualWebSerialApiProps {
    value: BoardValue;
    onChange: (v: BoardValue) => void;
    isDisabled?: boolean;
    onConnect?: (baud: number, board: 'orange' | 'blue') => void;
}

export default function BoardSelectorManualWebSerialApi({
    value,
    onChange,
    isDisabled,
    onConnect,
}: BoardSelectorManualWebSerialApiProps) {
    const options = useMemo(() => STATIC_OPTIONS, []);
    const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);

    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    useEffect(() => {
        if (value) {
            const found = options.find((o) => o.value === value) || null;
            setSelectedOption(found);
        } else {
            setSelectedOption(null);
        }
    }, [value, options]);

    return (
        <Select<OptionType, false>
            instanceId="board-manual-select"
            inputId="board-manual-select"
            options={options}
            components={{ SingleValue: SelectedView, Option: OptionItem }}
            className="font-notojp font-medium w-full min-w-full text-color-base"
            styles={customStyles}
            placeholder="使用するスクーミーボードを選択してください"
            noOptionsMessage={() => '該当オプションはありません'}
            isSearchable={false}
            isClearable={false}
            backspaceRemovesValue={false}
            isDisabled={isDisabled}
            value={selectedOption}
            onChange={(option: SingleValue<OptionType>) => {
                if (isDisabled) return;
                setSelectedOption(option ?? null);
                onChange(option ? option.value : null);
                if (option) {
                    const targetBaud = BOARD_CONFIG[option.value].baud;
                    if (onConnect) {
                        onConnect(targetBaud, option.value);
                    }
                }
            }}
            menuPortalTarget={mounted ? document.body : undefined}
            menuPosition="fixed"
            menuShouldScrollIntoView={false}
        />
    );
}
